import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiX } from "react-icons/fi";
import { getDealById, Deal } from "../../../actions/deals-actions";
import { checkout, CheckoutResponse } from "../../../actions/order-actions";
import { useSnack } from "../../../hooks/useSnack";
import SnackbarComponent from "../../../components/Snackbar/Snackbar";
import StripeCheckout from "../../../components/Stripe/StripeCheckout";
import confetti from "canvas-confetti";
import "./DealDetail.scss";

// Default placeholder image
const PLACEHOLDER_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80";

function DealDetail() {
    const { dealId } = useParams<{ dealId: string }>();
    const navigate = useNavigate();
    const snack = useSnack();

    const [deal, setDeal] = useState<Deal | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCheckingOut, setIsCheckingOut] = useState(false);
    const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(null);
    const [showStripeModal, setShowStripeModal] = useState(false);

    useEffect(() => {
        const fetchDeal = async () => {
            if (!dealId) {
                setError("Deal not found");
                setIsLoading(false);
                return;
            }

            try {
                const response = await getDealById(dealId);
                setDeal(response.data);
            } catch (err: any) {
                setError(err.response?.data || "Failed to load deal");
            } finally {
                setIsLoading(false);
            }
        };

        fetchDeal();
    }, [dealId]);

    const handleCheckout = async () => {
        if (!deal || !deal.productCode) return;

        setIsCheckingOut(true);
        try {
            const response = await checkout({ serviceCode: deal.productCode });
            setCheckoutData(response.data);
            setShowStripeModal(true);
        } catch (err: any) {
            snack.setMessage(err.response?.data || "Failed to start checkout");
            snack.setSeverity("error");
            snack.onOpen();
        } finally {
            setIsCheckingOut(false);
        }
    };

    // Confetti celebration function
    const fireConfetti = useCallback(() => {
        const duration = 1000;
        const end = Date.now() + duration;
        const colors = ["#ffffff", "#22c55e", "#3b82f6", "#f59e0b", "#ef4444"];

        const frame = () => {
            confetti({
                particleCount: 3,
                angle: 60,
                spread: 55,
                origin: { x: 0, y: 1 },
                colors: colors,
            });

            confetti({
                particleCount: 3,
                angle: 120,
                spread: 55,
                origin: { x: 1, y: 1 },
                colors: colors,
            });

            confetti({
                particleCount: 2,
                angle: 90,
                spread: 100,
                origin: { x: 0.5, y: 1 },
                colors: colors,
            });

            if (Date.now() < end) {
                requestAnimationFrame(frame);
            }
        };

        frame();
    }, []);

    const handlePaymentSuccess = () => {
        setShowStripeModal(false);
        setCheckoutData(null);
        fireConfetti();

        snack.setMessage("Payment successful! Thank you for your purchase.");
        snack.setSeverity("success");
        snack.onOpen();

        setTimeout(() => {
            navigate("/admin/profile?tab=upgrades");
        }, 3500);
    };

    const handlePaymentError = (error: string) => {
        snack.setMessage(error);
        snack.setSeverity("error");
        snack.onOpen();
    };

    const handleCloseStripeModal = () => {
        setShowStripeModal(false);
        setCheckoutData(null);
    };

    const handleClose = () => {
        navigate("/admin/upgrades");
    };

    // Loading state
    if (isLoading) {
        return (
            <div className="deal-loading dark-theme">
                <div className="loading-spinner" />
                <p>Loading deal...</p>
            </div>
        );
    }

    // Error state
    if (error || !deal) {
        return (
            <div className="deal-error dark-theme">
                <p className="deal-error__message">{error || "Deal not found"}</p>
                <button className="deal-error__button" onClick={handleClose}>
                    Back to Upgrades
                </button>
            </div>
        );
    }

    const heroImage = deal.photoUrl || PLACEHOLDER_IMAGE;

    return (
        <div className="deal-detail dark-theme">
            {/* Close Button */}
            <button className="deal-detail__close" onClick={handleClose}>
                <FiX size={22} />
            </button>

            {/* Hero Image */}
            <div className="deal-hero">
                <div
                    className="deal-hero__image"
                    style={{ backgroundImage: `url(${heroImage})` }}
                />
                <div className="deal-hero__overlay" />
            </div>

            {/* Content */}
            <div className="deal-content">
                {/* Title */}
                <h1 className="deal-content__title">{deal.title}</h1>

                {/* Category Badge */}
                {deal.category && (
                    <span className="deal-category">{deal.category}</span>
                )}

                {/* Price */}
                <div className="deal-price">
                    {deal.priceFrom && deal.priceFrom > deal.priceActual && (
                        <span className="deal-price__original">
                            ${deal.priceFrom.toFixed(2)}
                        </span>
                    )}
                    <span className="deal-price__current">
                        ${deal.priceActual.toFixed(2)} USD
                    </span>
                </div>

                {/* About Section */}
                <div className="deal-content__section">
                    <h2 className="deal-content__label">About</h2>
                    <p className="deal-content__text">
                        {deal.description || `Discover amazing offers at ${deal.title}. Don't miss this exclusive opportunity available only for our guests.`}
                    </p>
                </div>

                {/* Product Code */}
                {deal.productCode && (
                    <div className="deal-content__section">
                        <h2 className="deal-content__label">Product Code</h2>
                        <p className="deal-content__text">{deal.productCode}</p>
                    </div>
                )}
            </div>

            {/* Sticky Action Bar */}
            <div className="deal-action-bar">
                <div className="deal-action-bar__container">
                    <div className="deal-action-bar__label">Your upgrade:</div>
                    <div className="deal-action-bar__selection">
                        <span className="deal-action-bar__selection-title">{deal.title}</span>
                        <span className="deal-action-bar__selection-price">${deal.priceActual.toFixed(2)} USD</span>
                    </div>
                    <button
                        className="deal-action-bar__button"
                        onClick={handleCheckout}
                        disabled={isCheckingOut}
                    >
                        {isCheckingOut ? (
                            <>
                                <span className="loading-spinner" />
                                Processing...
                            </>
                        ) : (
                            "Purchase"
                        )}
                    </button>
                </div>
            </div>

            {/* Stripe Checkout Modal */}
            {showStripeModal && checkoutData && (
                <div className="stripe-modal">
                    <div className="stripe-modal__backdrop" onClick={handleCloseStripeModal} />
                    <div className="stripe-modal__content">
                        <div className="stripe-modal__header">
                            <h3>Complete Payment</h3>
                            <button className="stripe-modal__close" onClick={handleCloseStripeModal}>
                                <FiX size={18} />
                            </button>
                        </div>
                        <StripeCheckout
                            clientSecret={checkoutData.clientSecret}
                            amount={checkoutData.amount}
                            onSuccess={handlePaymentSuccess}
                            onError={handlePaymentError}
                        />
                    </div>
                </div>
            )}

            {/* Snackbar */}
            <SnackbarComponent
                isOpen={snack.isOpen}
                severity={snack.severity}
                onClose={snack.onClose}
                message={snack.message}
            />
        </div>
    );
}

export default DealDetail;
